/**
 * "My Plans" — member view of plans assigned by trainers.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ClipboardList, Utensils, Dumbbell } from "lucide-react";
import { apiClient } from "../../services/api";

export default function MyPlans() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => {
    apiClient.get("/me/plans").then((r) => setPlans(r.data)).catch(() => setPlans([]));
  }, []);

  const open = async (p) => {
    setActive(p);
    apiClient.post(`/me/plans/${p.assignment_id}/view`).catch(() => {});
  };

  return (
    <div className="app-page min-h-screen pb-12" data-testid="my-plans-page" style={{ background: "#F4F3F1" }}>
      <header className="sticky top-0 z-20 bg-canvas/85 backdrop-blur-xl">
        <div className="max-w-2xl mx-auto h-14 px-3 flex items-center gap-2">
          <button onClick={() => navigate(-1)} aria-label="Back" data-testid="my-plans-back" className="w-10 h-10 grid place-items-center"><ChevronLeft size={22} /></button>
          <h1 className="font-display text-[20px]">My Plans</h1>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 pt-4">
        {plans.length === 0 ? (
          <div className="text-center py-16">
            <ClipboardList size={36} className="text-ink-tertiary mx-auto" />
            <div className="font-barlow-cond text-[22px] font-bold mt-3">No plans yet</div>
            <div className="text-[13px] text-ink-soft mt-1">When a trainer sends you a plan, it'll show up here.</div>
          </div>
        ) : (
          <div className="space-y-2">
            {plans.map((a) => {
              const plan = a.plan_snapshot || {};
              const Icon = plan.plan_type === "workout" ? Dumbbell : Utensils;
              return (
                <button key={a.assignment_id} onClick={() => open(a)} data-testid={`plan-${a.assignment_id}`} className="w-full bg-white border border-line2 rounded-[14px] p-3 text-left flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full grid place-items-center" style={{ background: "rgba(0,212,255,0.18)", color: "#00B0D5" }}>
                    <Icon size={18} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-[14.5px] truncate">{plan.name}</div>
                    <div className="text-[11.5px] text-ink-tertiary">From {a.trainer_name || "your trainer"} · {(a.sent_at || "").slice(0, 10)}</div>
                  </div>
                  <span className="text-[10px] font-bold uppercase tracking-wider px-2 h-5 leading-5 rounded-pill" style={{ background: a.viewed_at ? "#ECEAE7" : "rgba(0,180,100,0.12)", color: a.viewed_at ? "#6B6760" : "rgba(0,150,80,1)", borderRadius: 100 }}>
                    {a.viewed_at ? plan.plan_type : "New"}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {active && (
          <div className="fixed inset-0 z-[70] flex items-end bg-black/40" onClick={() => setActive(null)} data-testid="plan-sheet">
            <div className="w-full bg-white rounded-t-[20px] max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="sticky top-0 bg-white z-10 px-5 pt-4 pb-2 border-b border-line2">
                <div className="w-10 h-1 bg-line2 rounded-full mx-auto mb-3" />
                <div className="text-[10.5px] uppercase tracking-wider font-bold text-ink-tertiary">From {active.trainer_name}</div>
                <h2 className="font-barlow-cond font-black text-[26px]">{active.plan_snapshot?.name}</h2>
                {active.note_to_member && <p className="text-[13px] text-ink-soft mt-1">"{active.note_to_member}"</p>}
              </div>
              <div className="p-5 space-y-3">
                {active.plan_snapshot?.plan_type === "workout" && (active.plan_snapshot?.workout_days || []).map((d, i) => (
                  <section key={i} className="bg-canvas2 rounded-[12px] p-3">
                    <h3 className="font-bold text-[14.5px] mb-2">{d.day_label}</h3>
                    <div className="space-y-1.5">
                      {(d.exercises || []).map((e, j) => (
                        <div key={j} className="flex justify-between text-[13px]">
                          <div className="flex-1 min-w-0">
                            <div className="font-medium truncate">{e.name}</div>
                            {e.notes && <div className="text-[11.5px] text-ink-tertiary truncate">{e.notes}</div>}
                          </div>
                          <div className="text-[12.5px] text-ink-soft shrink-0 ml-3">{e.sets}×{e.reps}{e.weight ? ` · ${e.weight}` : ""}</div>
                        </div>
                      ))}
                    </div>
                  </section>
                ))}
                {active.plan_snapshot?.plan_type === "meal" && (active.plan_snapshot?.meal_days || []).map((d, i) => (
                  <section key={i} className="bg-canvas2 rounded-[12px] p-3">
                    <h3 className="font-bold text-[14.5px] mb-2">{d.day_label}</h3>
                    <div className="space-y-1 text-[13px]">
                      {[["Breakfast", d.breakfast], ["Lunch", d.lunch], ["Dinner", d.dinner], ["Snacks", d.snacks]].filter(([, v]) => v).map(([k, v]) => (
                        <div key={k}><span className="font-semibold">{k}:</span> <span className="text-ink-soft">{v}</span></div>
                      ))}
                      {d.daily_calories && <div className="text-[11.5px] text-ink-tertiary mt-1">~{d.daily_calories} kcal target</div>}
                    </div>
                  </section>
                ))}
                {active.plan_snapshot?.notes && (
                  <p className="text-[13px] text-ink-soft p-3 bg-canvas2 rounded-[12px]">{active.plan_snapshot.notes}</p>
                )}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
